// System partners — the band above the footer of every Sportya page (ADR-0016). Event sponsors are a
// different model, edited on the event itself, and never reach this screen.
//
// Everything here is the shape of one PATCH /settings/partners write. It is separated from the page
// so the rules that matter — how the logo path is composed, when a website is legal, and what makes
// a write an add rather than an edit — are testable without a form.

import { assetSrc, logoFilename, normalizeWebsite } from "components/Common/SponsorBand/sponsorFields"

// Fixed in code, mirroring the API's PARTNER_TIERS (common/mongoDbSchema/schemaVariables): the Joi
// schema rejects anything else, so this list is the whole legal vocabulary. Order is display order.
export const PARTNER_TIERS = [
  { key: "coordinators", label: "Coordinators" },
  { key: "official", label: "Official Sponsors" },
  { key: "sponsors", label: "Sponsors" },
  { key: "media", label: "Media Partners" },
]

// Where the logo lives in storage. The gateway gates writes to this bucket to SuperAdmins.
export const PARTNER_LOGO_BUCKET = "common"
export const PARTNER_LOGO_FOLDER = "partners"
export const PARTNER_LOGO_PATH = `${PARTNER_LOGO_BUCKET}/${PARTNER_LOGO_FOLDER}`

// `countries` is an open list in the schema, but the deal it encodes is not: a partner either bought
// the whole platform or bought one country. Three choices cover every contract Sportya has signed,
// and an editor of the world's countries would be a screen of its own for a case that has not
// arrived. Rows storing anything else still read correctly — see `marketLabel`.
export const PARTNER_MARKETS = [
  { key: "all", label: "All markets", countries: [] },
  { key: "RO", label: "Romania only", countries: ["RO"] },
  { key: "BG", label: "Bulgaria only", countries: ["BG"] },
]

export const tierLabel = tier =>
  PARTNER_TIERS.find(({ key }) => key === tier)?.label || tier || ""

/** Which of the three choices a stored `countries` array corresponds to. */
export const marketKey = countries => {
  const list = Array.isArray(countries) ? countries.filter(Boolean) : []
  if (!list.length) return "all"
  return (
    PARTNER_MARKETS.find(
      ({ countries: market }) =>
        market.length === list.length && market.every(country => list.includes(country))
    )?.key || "custom"
  )
}

export const countriesForMarket = key =>
  PARTNER_MARKETS.find(market => market.key === key)?.countries || []

/** Reads back anything stored, including a combination the three choices cannot express. */
export const marketLabel = countries => {
  const list = Array.isArray(countries) ? countries.filter(Boolean) : []
  if (!list.length) return "all markets"
  return PARTNER_MARKETS.find(({ key }) => key === marketKey(list))?.label || list.join(", ")
}

// POST /assets/:bucket/:folder answers with a bare filename. The stored `logoUrl` is the path under
// /assets, not an absolute address: the same document has to serve DEV and production, where the API
// origin differs. Each consumer composes its own address from this.
export const partnerLogoUrl = filename =>
  filename ? `${PARTNER_LOGO_PATH}/${String(filename).trim()}` : ""

/** The bare filename back out of a stored path, for anything that needs it on its own. */
export const partnerLogoFilename = logoFilename

/**
 * CP's own address for a stored logo. A value with no folder in it is read as a bare filename in
 * the partners folder, which is what earlier writes stored; anything else is a path under /assets.
 */
export const partnerLogoSrc = logoUrl => {
  const stored = String(logoUrl || "").trim().replace(/^\/+/, "")
  if (!stored) return ""
  return assetSrc(stored.includes("/") ? stored : `${PARTNER_LOGO_PATH}/${stored}`)
}

// The website rule is the sponsor plate's, not this screen's — see components/Common/SponsorBand.
export { normalizeWebsite }

// Mongo hands back a full ISO instant; <input type="date"> only speaks calendar days. Cutting at the
// T is deliberate — going through a Date would shift the day for anyone east or west of UTC, and the
// contract ends on a date, not at an instant.
export const dateInputValue = value => (value ? String(value).slice(0, 10) : "")

/**
 * Builds the `data` of a PATCH /settings/partners write.
 *
 * `data` is the WHOLE partner, never a delta: the API replaces the stored item positionally and any
 * omitted field falls back to its model default. Sending `id` edits that partner in place; omitting
 * it is how a partner is added (the API mints the uuid).
 */
export const buildPartnerPayload = ({
  id,
  tier,
  name,
  logoFilename,
  logoUrl,
  website,
  order,
  active,
  endsAt,
  countries,
} = {}) => ({
  ...(id ? { id } : {}),
  tier,
  name: String(name ?? "").trim(),
  // An edit that did not re-upload keeps the stored path; a new upload wins.
  logoUrl: logoFilename ? partnerLogoUrl(logoFilename) : String(logoUrl ?? "").trim(),
  url: normalizeWebsite(website),
  order: Number.isFinite(Number(order)) ? Number(order) : 0,
  active: active !== false,
  // Omitted rather than nulled: a positional $set replaces the whole subdocument, so a field left
  // out lands on the model default — which for `endsAt` is no date at all. That is exactly what
  // clearing a contract means, and Joi would reject the empty string a date input hands back.
  ...(dateInputValue(endsAt) ? { endsAt: dateInputValue(endsAt) } : {}),
  countries: Array.isArray(countries) ? countries.filter(Boolean) : [],
})

/**
 * The same write, from a partner as the API stores it rather than as the form holds it. One place
 * knows that a stored partner says `url` where the form says `website`, so reordering and restoring
 * do not each reinvent the mapping.
 */
export const partnerWritePayload = (partner = {}) =>
  buildPartnerPayload({
    id: partner.id,
    tier: partner.tier,
    name: partner.name,
    logoUrl: partner.logoUrl,
    website: partner.url,
    order: partner.order,
    active: partner.active,
    endsAt: partner.endsAt,
    countries: partner.countries,
  })

/**
 * The form's fields for a partner — an existing one to edit, or nothing to add a fresh one. Kept
 * next to the payload builder because the two are opposite halves of the same mapping, and a field
 * added to one without the other is how an edit quietly drops a value.
 */
export const partnerFormValues = (partner = null, defaultTier = PARTNER_TIERS[0].key) => ({
  id: partner?.id || "",
  tier: partner?.tier || defaultTier,
  name: partner?.name || "",
  website: partner?.url || "",
  logoUrl: partner?.logoUrl || "",
  // Only ever set by a fresh upload: an edit that does not touch the logo keeps `logoUrl`.
  logoFilename: "",
  endsAt: dateInputValue(partner?.endsAt),
  market: marketKey(partner?.countries),
  countries: Array.isArray(partner?.countries) ? partner.countries.filter(Boolean) : [],
  active: partner?.active !== false,
})

/**
 * The countries a filled-in form means. A row storing a combination the three choices cannot express
 * keeps it: the form did not put it there and has no business flattening it to "all markets" just
 * because someone opened the partner to change its name.
 */
export const partnerFormCountries = ({ market, countries } = {}) =>
  market === "custom"
    ? (Array.isArray(countries) ? countries.filter(Boolean) : [])
    : countriesForMarket(market)

// The form asks for both a logo and a website (the decision of 2026-08-19), even though the API only
// insists on the logo. Returns the field keys that are missing, so the page can mark them.
export const partnerFormErrors = ({ tier, name, logoFilename, logoUrl, website } = {}) => {
  const errors = []
  if (!PARTNER_TIERS.some(({ key }) => key === tier)) errors.push("tier")
  if (!String(name ?? "").trim()) errors.push("name")
  if (!logoFilename && !String(logoUrl ?? "").trim()) errors.push("logo")
  if (!String(website ?? "").trim()) errors.push("website")
  return errors
}

// GET /admin/settings/partners answers '' for a category never written, and GET /settings simply omits it —
// so this must survive a non-array. Groups for display in taxonomy order, tiers with no partners
// included: the person administering them needs to see that a tier is empty, unlike the public band.
export const groupPartnersByTier = partners => {
  const rows = Array.isArray(partners) ? partners : []

  return PARTNER_TIERS.map(({ key, label }) => ({
    key,
    label,
    partners: rows
      .filter(partner => partner?.tier === key)
      .sort(
        (a, b) =>
          (a.order ?? 0) - (b.order ?? 0) ||
          String(a.name ?? "").localeCompare(String(b.name ?? ""))
      ),
  }))
}
